import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import config from "../../config";
import dayjs from "dayjs";
import {
  Table,
  Button,
  DatePicker,
  Form,
  Select,
  Alert,
  Checkbox,
} from "antd";
import { NavLink } from "react-router-dom";
import { PrinterOutlined } from "@ant-design/icons";
import ProductDropDown from "../Shared/ProductsDropDown";
import LoginChecked from "../Shared/LoginChecked";
import moment from "moment";
import { NumberFormat } from "../Shared/Utility";
import { parseReportAmount } from "../../Pdf/PrintFormateNumber";

function DateWiseProductSummary() {
  const { RangePicker } = DatePicker;
  const [loading, setLoading] = useState(false);
  const UserID = localStorage.getItem("ID");
  const AccessKey = localStorage.getItem("AccessKey");
  const [StartDate, SetStartDate] = useState(dayjs().subtract(1, "month"));
  const [EndDate, SetEndDate] = useState(dayjs());
  const [ProductID, setProductID] = useState(0);
  const [ProductSummary, setProductSummary] = useState([]);
  const [reportMeta, setReportMeta] = useState({});
  const [hideEmptyDays, setHideEmptyDays] = useState(true);
  const ProductType = ProductDropDown();

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);

  const productOptions = useMemo(() => {
    const base = Array.isArray(ProductType) ? ProductType : [];
    return [{ value: 0, label: "All Products" }, ...base];
  }, [ProductType]);

  const daySpan =
    StartDate && EndDate ? dayjs(EndDate).diff(dayjs(StartDate), "day") + 1 : 0;
  const showAllProductsWarning = ProductID === 0 && daySpan > 31;

  useEffect(() => {
    document.title = "Datewise Product Summary";
    setLoading(true);

    axios({
      method: "post",
      url: config.base_url + "Report/GetDateWiseProductSummary",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        PageNo: pageNo,
        PageSize: pageSize,
        StartDate: dayjs(StartDate).format("YYYY-MM-DD"),
        EndDate: dayjs(EndDate).format("YYYY-MM-DD"),
        ProductID: ProductID || 0,
        HideZeroBalance: hideEmptyDays,
      },
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setProductSummary(response.data.ListofSummary || []);
          setReportMeta(response.data);
          setTotalRecords(response.data.totalRecords || 0);
        } else {
          setProductSummary([]);
          setReportMeta({});
          setTotalRecords(0);
        }
        setLoading(false);
      })
      .catch(function () {
        setProductSummary([]);
        setReportMeta({});
        setTotalRecords(0);
        setLoading(false);
      });
  }, [StartDate, EndDate, ProductID, pageNo, pageSize, hideEmptyDays, UserID, AccessKey]);

  const fmtQty = (val) => NumberFormat(parseReportAmount(val));
  const fmtAmt = (val) => NumberFormat(parseReportAmount(val), 0);

  const columns = useMemo(() => {
    const cols = [
      {
        title: "Sr.#",
        key: "sr",
        width: 50,
        render: (_, __, index) => (pageNo - 1) * pageSize + index + 1,
      },
    ];

    if (ProductID === 0) {
      cols.push({
        title: "Product",
        dataIndex: "ProductName",
        key: "ProductName",
        width: 140,
      });
    }

    cols.push(
      {
        title: "Date",
        dataIndex: "Dated",
        key: "Dated",
        width: 110,
        render: (date) =>
          date ? moment(date).format(config.date_format) : "—",
      },
      {
        title: "Op. Stock",
        dataIndex: "OpeningStock",
        key: "OpStock",
        align: "right",
        render: (val) => fmtQty(val),
      },
      {
        title: "Op. Value",
        dataIndex: "OpeningStockValue",
        key: "OpValue",
        align: "right",
        render: (val) => fmtAmt(val),
      },
      {
        title: "Purchases",
        dataIndex: "Purchase",
        key: "Purchases",
        align: "right",
        render: (val) => fmtQty(val),
      },
      {
        title: "Purchases Value",
        dataIndex: "PurchaseValue",
        key: "PurchasesValue",
        align: "right",
        render: (val) => fmtAmt(val),
      },
      {
        title: "Sales",
        dataIndex: "Sales",
        key: "Sales",
        align: "right",
        render: (val) => fmtQty(val),
      },
      {
        title: "Sales Value",
        dataIndex: "SalesValue",
        key: "SalesValue",
        align: "right",
        render: (val) => fmtAmt(val),
      },
      {
        title: "Closing Stock",
        dataIndex: "ClosingStock",
        key: "ClosingStock",
        align: "right",
        render: (val) => fmtQty(val),
      },
      {
        title: "Closing Stock Value",
        dataIndex: "ClosingStockValue",
        key: "ClosingStockValue",
        align: "right",
        render: (val) => fmtAmt(val),
      }
    );

    return cols;
  }, [ProductID, pageNo, pageSize]);

  const handleDateChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      SetStartDate(dates[0]);
      SetEndDate(dates[1]);
      setPageNo(1);
    }
  };

  const handleProductChange = (value) => {
    setProductID(value ?? 0);
    setPageNo(1);
  };

  const disableDate = (current) =>
    current && current > dayjs().endOf("day");

  return (
    <>
      <LoginChecked />
      <div className="right-side-contents">
        <div className="page-content">
          <div className="page-header">
            <h3 className="page-title">Datewise Product Summary</h3>

            <div className="header-actions">
              <NavLink
                to={`/report-datewiseProductSummary-all/print-list/?StartDate=${dayjs(
                  StartDate
                ).format("YYYY-MM-DD")}&EndDate=${dayjs(EndDate).format(
                  "YYYY-MM-DD"
                )}&ProductID=${ProductID || 0}&HideZeroBalance=${
                  hideEmptyDays ? "1" : "0"
                }`}
                target="_blank"
              >
                <Button type="dashed" size="large" shape="round">
                  <PrinterOutlined />
                  Print Report
                </Button>
              </NavLink>
            </div>
          </div>

          {showAllProductsWarning && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Large date range with all products"
              description="This can produce many rows and load slowly. Select a single product or a shorter date range for faster results."
            />
          )}

          <div className="filters-wrap">
            <Form layout="inline">
              <Form.Item label="Period">
                <RangePicker
                  value={[dayjs(StartDate), dayjs(EndDate)]}
                  onChange={handleDateChange}
                  disabledDate={disableDate}
                />
              </Form.Item>
              <Form.Item label="Product">
                <Select
                  showSearch
                  allowClear
                  style={{ minWidth: 220 }}
                  placeholder="All Products"
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={ProductID || undefined}
                  onChange={handleProductChange}
                  options={productOptions}
                />
              </Form.Item>
              <Form.Item>
                <Checkbox
                  checked={hideEmptyDays}
                  onChange={(e) => {
                    setHideEmptyDays(e.target.checked);
                    setPageNo(1);
                  }}
                >
                  Hide days with no activity
                </Checkbox>
              </Form.Item>
            </Form>
          </div>

          <Table
            size="small"
            scroll={{ x: "max-content" }}
            bordered
            loading={loading}
            columns={columns}
            dataSource={ProductSummary}
            rowKey={(record, index) =>
              `${record.ProductName || "p"}-${record.Dated}-${index}`
            }
            pagination={{
              current: pageNo,
              pageSize,
              total: totalRecords,
              showSizeChanger: true,
              pageSizeOptions: ["25", "50", "100", "200"],
              onChange: (page, size) => {
                setPageNo(page);
                setPageSize(size);
              },
            }}
            summary={() =>
              ProductSummary.length > 0 ? (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={ProductID === 0 ? 5 : 4}
                      align="right"
                    >
                      <strong>Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <strong>{fmtQty(reportMeta.TotalPurchaseQty)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <strong>{fmtAmt(reportMeta.TotalPurchaseValue)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <strong>{fmtQty(reportMeta.TotalSaleQty)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <strong>{fmtAmt(reportMeta.TotalSaleValue)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} colSpan={2} />
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
        </div>
      </div>
    </>
  );
}

export default DateWiseProductSummary;
